import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, MapPin, Flag, ArrowRight, UsersThree, X, Plus, Star, Camera, WarningCircle, UploadSimple as Upload, Lock } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import ConfidenceBadge from "../components/ConfidenceBadge";

export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState("");
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [fourball, setFourball] = useState(false);
  // guests = additional players in a fourball. Each slot has a stable
  // React key (id), a name, and an optional handicap index so their
  // own net-scoring uses the right value.
  const [guests, setGuests] = useState([{ id: "g1", name: "", handicap: "" }]);
  const { user, toggleFavourite } = useAuth();
  const isFav = (user?.favourite_course_ids || []).includes(id);
  const isPro =
    user?.role === "admin" ||
    ["active", "trialing"].includes(user?.subscription_status);
  // Free tier is locked to yellow / default. Backend enforces the same set.
  const FREE_TEE_IDS = new Set(["", "yellow"]);
  // Crowdsourced course-stats submission (only relevant when the course has
  // unknown stats — see the modal toggled by `showStatsModal`).
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [submitPar, setSubmitPar] = useState("72");
  const [submitYds, setSubmitYds] = useState("6500");
  // Primary tee colour (the longest tee on the card — drives par/yards totals shown above).
  const [submitTee, setSubmitTee] = useState("");
  // Optional WHS rating/slope for the primary tee (from OCR or manual entry)
  const [submitCourseRating, setSubmitCourseRating] = useState("");
  const [submitSlopeRating, setSubmitSlopeRating] = useState("");
  // Additional tees on the same scorecard. Each entry: { label, holes: [{number,yards}] }.
  // Primary tee's per-hole yards still live on `perHole[i].yards`. Extra tees stack on top.
  const [extraTees, setExtraTees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  // Photo-extract state: user uploads a phone snap of the scorecard, Gemini
  // vision reads it, and we pre-fill the per-hole grid so they don't type 36 numbers.
  const [extracting, setExtracting] = useState(false);
  const [extractMsg, setExtractMsg] = useState("");
  // OCR confidence from the last extract — forwarded to /submit-stats so the
  // backend can auto-approve high-confidence reads without an admin.
  const [ocrConfidence, setOcrConfidence] = useState(null);

  // Reused by both "Take photo" (camera) and "Upload" (gallery) inputs in the modal.
  const handleExtractFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-pick of the same file
    if (!file) return;
    setExtracting(true);
    setExtractMsg("");
    setSubmitMsg("");
    try {
      const form = new FormData();
      form.append("image", file);
      const { data } = await api.post(
        `/courses/${id}/extract-scorecard`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSubmitPar(String(data.par_total));
      setSubmitYds(String(data.yards_total));
      const holesArr = Array.from({ length: 18 }, (_, i) => {
        const match = (data.holes || []).find((h) => h.number === i + 1);
        return match
          ? { number: i + 1, par: match.par, yards: match.yards, stroke_index: match.stroke_index ?? null }
          : { number: i + 1, par: 4, yards: 380, stroke_index: null };
      });
      setPerHole(holesArr);
      setShowPerHole(true);
      // Multi-tee: the LONGEST tee becomes the primary tee, every other tee
      // gets stacked into `extraTees` so the user can submit them all at once.
      const aiTees = Array.isArray(data.tees) ? data.tees : [];
      const primaryLabel = (data.tee_label || (aiTees[0]?.label) || "").toLowerCase();
      if (primaryLabel) setSubmitTee(primaryLabel);
      // Capture WHS rating/slope for the primary tee if OCR could read them
      const primaryTeeData = aiTees.find((t) => t.label?.toLowerCase() === primaryLabel);
      setSubmitCourseRating(primaryTeeData?.course_rating != null ? String(primaryTeeData.course_rating) : "");
      setSubmitSlopeRating(primaryTeeData?.slope_rating != null ? String(primaryTeeData.slope_rating) : "");
      const extras = aiTees
        .filter((t) => t.label && t.label.toLowerCase() !== primaryLabel)
        .map((t) => ({
          label: t.label.toLowerCase(),
          course_rating: t.course_rating ?? null,
          slope_rating: t.slope_rating ?? null,
          holes: Array.from({ length: 18 }, (_, i) => {
            const match = (t.holes || []).find((h) => h.number === i + 1);
            return { number: i + 1, yards: match?.yards ?? 380 };
          }),
        }));
      setExtraTees(extras);
      const conf = data.confidence || "medium";
      setOcrConfidence(conf);
      const teeCount = (primaryLabel ? 1 : 0) + extras.length;
      const teeSummary = teeCount > 1
        ? ` (${teeCount} tees: ${[primaryLabel, ...extras.map((t) => t.label)].filter(Boolean).join(", ")})`
        : (primaryLabel ? ` (${primaryLabel} tee)` : "");
      const siCount = (data.holes || []).filter((h) => h.stroke_index !== null && h.stroke_index !== undefined).length;
      const siSummary = siCount === 18 ? " · SI 1–18 read" : siCount > 0 ? ` · SI partial (${siCount}/18)` : "";
      const ratingSummary = primaryTeeData?.course_rating != null && primaryTeeData?.slope_rating != null
        ? ` · CR ${primaryTeeData.course_rating}/Slope ${primaryTeeData.slope_rating}`
        : "";
      setExtractMsg(
        conf === "low"
          ? `⚠ Read the card but I'm not confident${teeSummary}${siSummary}${ratingSummary} — please check each row before submitting.`
          : `✓ Got it${teeSummary}${siSummary}${ratingSummary}. Have a quick scroll to double-check before submitting.`
      );
    } catch (err) {
      setExtractMsg(formatApiError(err));
    } finally {
      setExtracting(false);
    }
  };
  // Optional per-hole breakdown for the submission. Defaults to the
  // standard 18-hole par-4 / 380 yds template so the user only has to
  // tweak what's different. Toggle expands the modal to show all 18 rows.
  const [showPerHole, setShowPerHole] = useState(false);
  const [perHole, setPerHole] = useState(
    () => Array.from({ length: 18 }, (_, i) => ({ number: i + 1, par: 4, yards: 380, stroke_index: null })),
  );
  // Tee colour — remembered from last round for quick re-use
  const [teeColor, setTeeColor] = useState(
    () => localStorage.getItem("fairwayTeeColor") || ""
  );
  // Scoring mode (strokeplay default). Persisted per-device for convenience.
  const [scoringMode, setScoringMode] = useState(
    () => localStorage.getItem("fairwayScoringMode") || "strokeplay"
  );
  // Stableford net toggle — only relevant when scoringMode === "stableford".
  const [stablefordNet, setStablefordNet] = useState(
    () => localStorage.getItem("fairwayStablefordNet") === "1"
  );

  // If a free user has a stale Pro-only tee saved, snap them back to yellow.
  useEffect(() => {
    if (user && !isPro && teeColor && !FREE_TEE_IDS.has(teeColor)) {
      setTeeColor("yellow");
    }
  }, [user, isPro]);

  // The current user's most recent submission for this course (any status).
  // When pending, we paint its par/yards over the placeholder so the user
  // sees their submission immediately without waiting for admin approval.
  const [mySubmission, setMySubmission] = useState(null);
  // Keep the raw course separate so we can recompute the overlay when the
  // submission arrives.
  const [rawCourse, setRawCourse] = useState(null);
  // Trust badge signals for this course — separate endpoint so the main
  // course payload stays lean.
  const [confidence, setConfidence] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, l] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get("/leagues/mine"),
      ]);
      setRawCourse(c.data);
      setLeagues(l.data);
      const matches = (l.data || []).filter((lg) =>
        Array.isArray(lg.required_courses) && lg.required_courses.some((rc) => rc.course_id === id)
      );
      if (matches.length === 1) setLeagueId(matches[0].id);
    })();
    (async () => {
      try {
        const { data } = await api.get(`/courses/${id}/my-submission`);
        setMySubmission(data.submission || null);
      } catch {
        setMySubmission(null);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get(`/courses/${id}/confidence`);
        setConfidence(data);
      } catch {
        setConfidence(null);
      }
    })();
  }, [id]);

  // Derive the rendered course by overlaying the user's pending submission
  // onto the raw course doc. For a pending submission, replace each hole's
  // par + yards with the submitted values so the Scorecard table shows the
  // user's own numbers instead of the "~4 / ~400" placeholders. Other
  // fields (name, location, hole lat/lng, etc.) are preserved.
  const course = useMemo(() => {
    if (!rawCourse) return null;
    const sub = mySubmission?.status === "pending" ? mySubmission : null;
    if (!sub) return rawCourse;
    const hasHoles = Array.isArray(sub.holes) && sub.holes.length > 0;
    const byNum = hasHoles ? Object.fromEntries(sub.holes.map((h) => [h.number, h])) : {};
    const merged = hasHoles
      ? (rawCourse.holes || []).map((h) => {
          const s = byNum[h.number];
          return s ? { ...h, par: s.par, yards: s.yards } : h;
        })
      : rawCourse.holes;
    return {
      ...rawCourse,
      holes: merged,
      par_total: sub.par_total ?? rawCourse.par_total,
      yards_total: sub.yards_total ?? rawCourse.yards_total,
      stats_unknown: false,
    };
  }, [rawCourse, mySubmission]);

  const updateGuest = (idx, field, value) => {
    setGuests((g) => g.map((v, i) => (i === idx ? { ...v, [field]: value } : v)));
  };
  const addGuest = () => {
    setGuests((g) => (g.length >= 3 ? g : [...g, { id: `g${Date.now()}`, name: "", handicap: "" }]));
  };
  const removeGuest = (idx) => {
    setGuests((g) => (g.length <= 1 ? [{ id: "g1", name: "", handicap: "" }] : g.filter((_, i) => i !== idx)));
  };

  const startRound = async () => {
    setError("");
    setStarting(true);
    try {
      // Build the multi-field guest payload — one slot per mate with
      // their name + optional handicap. Backend prefers `guest_players`
      // over the legacy string-only `guests` array.
      const cleanedPlayers = fourball
        ? guests
            .map((g) => ({
              name: g.name.trim(),
              // Handicap: coerce to a bounded WHS-legal float or null.
              handicap: g.handicap !== "" && g.handicap != null && !Number.isNaN(Number(g.handicap))
                ? Math.max(0, Math.min(54, Number(g.handicap)))
                : null,
            }))
            .filter((g) => g.name)
            .slice(0, 3)
        : [];
      const payload = {
        course_id: id,
        league_id: leagueId || null,
        guests: cleanedPlayers.length ? cleanedPlayers.map((g) => g.name) : null,       // legacy compat
        guest_players: cleanedPlayers.length ? cleanedPlayers : null,
        tee_color: teeColor || null,
        scoring_mode: scoringMode,
      };
      if (scoringMode === "stableford") {
        payload.stableford_net = !!stablefordNet && user?.handicap != null;
      }
      const { data } = await api.post("/rounds", payload);
      if (teeColor) localStorage.setItem("fairwayTeeColor", teeColor);
      // Cache for offline play — round screen falls back to these if API is unreachable.
      try {
        localStorage.setItem(`fairway:cached-round:${data.id}`, JSON.stringify(data));
        localStorage.setItem(`fairway:cached-course:${id}`, JSON.stringify(course));
      } catch {}
      nav(`/app/round/${data.id}`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setStarting(false);
    }
  };

  if (!course) {
    return <div className="px-5 pt-6 text-[var(--text-secondary)]">Loading course...</div>;
  }

  // Convenience: pending = user has a queued submission for this course.
  // Used for the banner switch + the "ESTIMATED" → "PENDING" badge.
  const pendingSub = mySubmission?.status === "pending" ? mySubmission : null;

  return (
    <div className="pb-6">
      {/* Hero */}
      <div className="relative h-56">
        {course.image_url && (
          <img src={course.image_url} alt={course.name} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <Link to="/app/courses" className="absolute top-4 left-4 h-10 w-10 rounded-full bg-white/85 backdrop-blur flex items-center justify-center" data-testid="course-back-link">
          <ArrowLeft size={20} weight="bold" />
        </Link>
        <button
          type="button"
          onClick={() => toggleFavourite(id)}
          data-testid="course-favourite-toggle"
          aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
          className="absolute top-4 right-4 h-10 w-10 rounded-full backdrop-blur flex items-center justify-center transition-colors"
          style={{
            background: isFav ? "var(--accent-terracotta)" : "rgba(255,255,255,0.85)",
            color: isFav ? "#F2EEE7" : "var(--primary)",
          }}
        >
          <Star size={20} weight={isFav ? "fill" : "regular"} />
        </button>
        <div className="absolute bottom-4 left-5 right-5 text-white">
          <div className="flex items-center gap-1 text-xs uppercase tracking-wider opacity-90 mb-1">
            <MapPin size={14} weight="fill" /> {course.location}
          </div>
          <h1 className="font-heading font-black tracking-tighter-2 text-3xl leading-tight" data-testid="course-name">{course.name}</h1>
        </div>
      </div>

      <div className="px-5 pt-4">
        {course.description && <p className="text-[var(--text-secondary)] mb-4">{course.description}</p>}

        <div className="grid grid-cols-3 gap-3 mb-5">
          <Metric label="Par" value={course.par_total ?? "—"} testid="course-par" />
          <Metric label="Yards" value={course.yards_total ?? "—"} testid="course-yards" />
          <Metric label="Holes" value={course.holes.length} testid="course-holes-count" />
        </div>
        {confidence && (
          <div className="mb-5 rounded-2xl bg-[var(--secondary)] p-3" data-testid="course-confidence-card">
            <ConfidenceBadge confidence={confidence} size="md" showTip />
            {confidence.tier !== "green" && (
              <>
                {/* Checklist of remaining work — greens tick as they're done */}
                <div className="mt-3 space-y-1.5" data-testid="course-verify-checklist">
                  <ChecklistItem
                    done={confidence.scorecard_verified}
                    text={confidence.scorecard_verified
                      ? "Scorecard verified"
                      : "Upload scorecard for par + yardages"}
                  />
                  <ChecklistItem
                    done={confidence.gps_holes >= Math.max(1, Math.round((confidence.total_holes || 18) * 0.9))}
                    text={
                      confidence.gps_holes >= (confidence.total_holes || 18)
                        ? `GPS pins on all ${confidence.total_holes} holes`
                        : `GPS pins: ${confidence.gps_holes}/${confidence.total_holes || 18} · confirm on the green during a round`
                    }
                  />
                </div>
                {!confidence.scorecard_verified && (
                  <button
                    type="button"
                    onClick={() => setShowStatsModal(true)}
                    data-testid="course-verify-cta"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[var(--primary)] text-white font-heading font-bold text-sm hover:bg-[var(--primary)]/90 transition-colors"
                  >
                    <Camera size={16} weight="fill" /> Help verify this course
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {pendingSub ? (
          <div
            className="mb-5 rounded-2xl border-2 border-[var(--primary)] bg-[var(--accent-sand)] p-4"
            data-testid="submission-pending-banner"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 h-9 w-9 rounded-full bg-[var(--primary)] text-white flex items-center justify-center">
                <Camera size={18} weight="fill" />
              </div>
              <div className="flex-1">
                <div className="font-heading font-bold text-[var(--primary)] mb-0.5">
                  Your scorecard · Pending review
                </div>
                <p className="text-xs text-[var(--text)] leading-snug mb-3">
                  Showing your submitted par + yards below. We&apos;ll publish it to everyone once an admin reviews it (usually within a day).
                </p>
                <button
                  type="button"
                  onClick={() => setShowStatsModal(true)}
                  data-testid="submit-stats-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border-2 border-[var(--primary)] text-[var(--primary)] font-heading font-bold text-sm hover:bg-[var(--primary)]/10 transition-colors"
                >
                  <Camera size={18} weight="fill" /> Resubmit / fix values
                </button>
              </div>
            </div>
          </div>
        ) : course.stats_unknown && (
          <div
            className="mb-5 rounded-2xl border-2 border-[var(--accent-terracotta)] bg-[var(--accent-sand)] p-4"
            data-testid="stats-unknown-banner"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 h-9 w-9 rounded-full bg-[var(--accent-terracotta)] text-white flex items-center justify-center">
                <WarningCircle size={20} weight="bold" />
              </div>
              <div className="flex-1">
                <div className="font-heading font-bold text-[var(--primary)] mb-0.5">
                  Approximate scorecard
                </div>
                <p className="text-xs text-[var(--text)] leading-snug mb-3">
                  The par + yards below are a placeholder. GPS distances are still accurate.
                  Snap a photo of the real scorecard and we&apos;ll fix it for everyone.
                </p>
                <button
                  type="button"
                  onClick={() => setShowStatsModal(true)}
                  data-testid="submit-stats-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[var(--primary)] text-white font-heading font-bold text-sm hover:bg-[var(--primary)]/90 transition-colors"
                >
                  <Camera size={18} weight="fill" /> Add the real scorecard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tee colour picker — remembered between rounds.
            Free users get Default + Yellow only; other colours show a Pro lock
            and route to /subscribe on tap. Backend enforces the same gate. */}
        <div className="surface-card-flat p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="label-uppercase">Tee colour</label>
            {!isPro && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[var(--accent-terracotta)]">
                <Lock size={10} weight="bold" /> Pro for all colours
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="tee-picker">
            {[
              { id: "", label: "Default", swatch: "transparent", textOnSwatch: "var(--text)" },
              { id: "white", label: "White", swatch: "#ffffff", textOnSwatch: "#143D2F" },
              { id: "yellow", label: "Yellow", swatch: "#FFE45E", textOnSwatch: "#143D2F" },
              { id: "blue", label: "Blue", swatch: "#3B82F6", textOnSwatch: "#fff" },
              { id: "red", label: "Red", swatch: "#ef4444", textOnSwatch: "#fff" },
              { id: "black", label: "Black", swatch: "#111", textOnSwatch: "#fff" },
              { id: "gold", label: "Gold", swatch: "#D4AF37", textOnSwatch: "#143D2F" },
              { id: "green", label: "Green", swatch: "#16a34a", textOnSwatch: "#fff" },
            ].map((t) => {
              const active = teeColor === t.id;
              const locked = !isPro && !FREE_TEE_IDS.has(t.id);
              const handleClick = () => {
                if (locked) {
                  nav("/subscribe");
                  return;
                }
                setTeeColor(t.id);
              };
              return (
                <button
                  key={t.id || "default"}
                  type="button"
                  onClick={handleClick}
                  data-testid={`tee-${t.id || "default"}${locked ? "-locked" : ""}`}
                  aria-label={locked ? `${t.label} tee — Pro only` : t.label}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold transition-all"
                  style={{
                    background: active ? "var(--primary)" : "var(--bg-app)",
                    color: active ? "#F2EEE7" : "var(--text)",
                    border: active ? "1.5px solid var(--primary)" : "1.5px solid var(--border)",
                    opacity: locked ? 0.55 : 1,
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="inline-block w-3.5 h-3.5 rounded-full"
                    style={{
                      background: t.swatch,
                      border: t.id === "white" || t.id === "" ? "1px solid var(--border)" : "none",
                    }}
                  />
                  {t.label}
                  {locked && (
                    <Lock size={11} weight="bold" className="text-[var(--accent-terracotta)]" />
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-[var(--text-secondary)] mt-2 italic">
            {isPro
              ? "We'll remember this for next time. If we have yardages for this tee, the watch + scorecard will use them automatically."
              : "Free accounts play from the Yellow tees. Go Pro to play from any tee colour."}
          </p>
        </div>

        {leagues.length > 0 && (
          <div className="surface-card-flat p-4 mb-4">
            <label className="label-uppercase block mb-2">Play for league (optional)</label>
            <select
              value={leagueId}
              onChange={(e) => setLeagueId(e.target.value)}
              data-testid="select-league-for-round"
              className="input-field"
            >
              <option value="">Casual round</option>
              {leagues.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Scoring mode — strokeplay default, switch to Stableford or Match play.
            Stableford has a Net toggle (uses player handicap + per-hole stroke
            index when present, even-spread fallback otherwise). Match play is
            singles (1v1) when no guests, fourball best-ball (2v2) when guests
            are added below. */}
        <div className="surface-card-flat p-4 mb-4" data-testid="scoring-mode-picker">
          <label className="label-uppercase block mb-2">Scoring mode</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "strokeplay", label: "Strokeplay", desc: "Total strokes" },
              { id: "stableford", label: "Stableford", desc: "Points per hole" },
              { id: "match_play", label: "Match play", desc: fourball ? "Best-ball 2v2" : "1v1 head-to-head" },
            ].map((m) => {
              const active = scoringMode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    setScoringMode(m.id);
                    localStorage.setItem("fairwayScoringMode", m.id);
                  }}
                  data-testid={`scoring-mode-${m.id}`}
                  className="rounded-2xl p-3 text-left border-2 transition-colors"
                  style={{
                    background: active ? "var(--primary)" : "var(--bg-app)",
                    color: active ? "#F2EEE7" : "var(--text)",
                    borderColor: active ? "var(--primary)" : "var(--border)",
                  }}
                >
                  <div className="font-heading font-bold text-sm leading-tight">{m.label}</div>
                  <div className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">{m.desc}</div>
                </button>
              );
            })}
          </div>

          {scoringMode === "stableford" && (
            <div className="mt-3 flex items-center justify-between rounded-2xl px-3 py-2.5"
                 style={{ background: "var(--accent-sand)" }}
                 data-testid="stableford-net-row">
              <div className="min-w-0">
                <div className="font-heading font-bold text-[var(--primary)] text-sm">Net stableford</div>
                <div className="text-[11px] text-[var(--text-secondary)]">
                  {user?.handicap == null
                    ? "Add a handicap on Profile to enable"
                    : `Apply your ${user.handicap} handicap on each hole`}
                </div>
              </div>
              <button
                type="button"
                disabled={user?.handicap == null}
                onClick={() => {
                  const next = !stablefordNet;
                  setStablefordNet(next);
                  localStorage.setItem("fairwayStablefordNet", next ? "1" : "0");
                }}
                data-testid="stableford-net-toggle"
                className="text-[11px] uppercase font-bold tracking-wider px-3 py-1.5 rounded-full transition-colors"
                style={{
                  background: stablefordNet && user?.handicap != null ? "var(--primary)" : "white",
                  color: stablefordNet && user?.handicap != null ? "#F2EEE7" : "var(--text)",
                  border: "1.5px solid var(--border)",
                  opacity: user?.handicap == null ? 0.5 : 1,
                }}
              >
                {stablefordNet && user?.handicap != null ? "Net" : "Gross"}
              </button>
            </div>
          )}

          {scoringMode === "match_play" && (
            <p className="text-[11px] text-[var(--text-secondary)] mt-3">
              {fourball
                ? "Auto-teams: you + first guest vs the other two. Best ball wins each hole."
                : "Add one mate below to play head-to-head. Add three for fourball (2v2)."}
            </p>
          )}
        </div>

        {/* Fourball setup */}
        <div className="surface-card-flat p-4 mb-4">
          <button
            type="button"
            onClick={() => setFourball((v) => !v)}
            data-testid="toggle-fourball"
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-xl flex items-center justify-center"
                style={{ background: fourball ? "var(--primary)" : "var(--accent-sand)", color: fourball ? "#F2EEE7" : "var(--primary)" }}
              >
                <UsersThree size={20} weight="bold" />
              </div>
              <div>
                <div className="font-heading font-bold text-[var(--primary)]">Fourball mode</div>
                <div className="text-xs text-[var(--text-secondary)]">
                  {fourball ? "One device, scores for the group" : "Add up to 3 mates · score them all from this phone"}
                </div>
              </div>
            </div>
            <div
              className="text-xs uppercase font-bold tracking-wider"
              style={{ color: fourball ? "var(--accent-terracotta)" : "var(--text-secondary)" }}
            >
              {fourball ? "ON" : "OFF"}
            </div>
          </button>

          {fourball && (
            <div className="mt-4 space-y-2" data-testid="fourball-guest-list">
              {guests.map((g, i) => (
                <div key={g.id} className="flex items-center gap-2">
                  <span
                    className="font-mono-num font-bold text-xs w-6 text-center rounded-md py-1"
                    style={{ background: "var(--accent-sand)", color: "var(--primary)" }}
                  >
                    P{i + 2}
                  </span>
                  <input
                    type="text"
                    value={g.name}
                    onChange={(e) => updateGuest(i, "name", e.target.value)}
                    placeholder={`Mate ${i + 1} name`}
                    maxLength={40}
                    data-testid={`fourball-guest-input-${i}`}
                    className="input-field flex-1 min-w-0"
                  />
                  {/* Optional per-mate handicap so their Stableford / net
                      scores use their own WHS index rather than the host's.
                      Left blank → scored gross-only for that mate. */}
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min={0}
                    max={54}
                    value={g.handicap}
                    onChange={(e) => updateGuest(i, "handicap", e.target.value)}
                    placeholder="HCP"
                    aria-label={`Mate ${i + 1} handicap`}
                    data-testid={`fourball-guest-handicap-${i}`}
                    className="input-field !w-16 text-center font-mono-num"
                  />
                  {guests.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeGuest(i)}
                      aria-label="Remove mate"
                      data-testid={`fourball-remove-guest-${i}`}
                      className="h-9 w-9 shrink-0 rounded-xl flex items-center justify-center"
                      style={{ background: "var(--bg-app)" }}
                    >
                      <X size={16} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
              {guests.length < 3 && (
                <button
                  type="button"
                  onClick={addGuest}
                  data-testid="fourball-add-guest"
                  className="flex items-center gap-2 text-sm font-heading font-bold text-[var(--primary)] mt-1"
                >
                  <Plus size={14} weight="bold" /> Add another mate
                </button>
              )}
              <p className="text-[11px] text-[var(--text-secondary)] mt-2">
                Tip: guest scores stay on this round only — they won&apos;t affect a guest&apos;s personal stats. Add their WHS handicap so their net Stableford is calculated properly.
              </p>
            </div>
          )}
        </div>

        {error && <div className="text-sm text-[var(--status-bad)] bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3">{error}</div>}

        <button onClick={startRound} disabled={starting} className="btn-primary w-full text-lg mb-2" data-testid="start-round-button">
          <Flag size={22} weight="fill" /> {starting ? "Starting..." : "Start round"} <ArrowRight size={20} weight="bold" />
        </button>
        <p
          className="text-[11px] text-center text-[var(--text-secondary)] mb-6 px-3 leading-snug"
          data-testid="start-round-offline-tip"
        >
          Tip: tap <strong>Start</strong> while you&apos;ve still got signal in the car park.
          Once started, your phone keeps every score locally and syncs the moment you&apos;re back online — no signal needed on the course.
        </p>

        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg text-[var(--primary)]">Scorecard</h3>
          {pendingSub ? (
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[var(--primary)] text-white"
              data-testid="scorecard-pending-badge"
            >
              Pending review
            </span>
          ) : course.stats_unknown && (
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[var(--accent-terracotta)] text-white"
              data-testid="scorecard-placeholder-badge"
            >
              Estimated
            </span>
          )}
        </div>
        <div
          className={`surface-card-flat overflow-hidden ${course.stats_unknown ? "ring-2 ring-[var(--accent-terracotta)]/40" : ""}`}
          data-testid="scorecard-table"
        >
          {(() => {
            // Build the per-tee column list. We collect any colour that has yardage
            // on at least one hole, sorted longest → shortest by total yards.
            const teeColors = (() => {
              const seen = new Map(); // color -> total yards
              for (const h of course.holes) {
                for (const t of (h.tees || [])) {
                  const c = (t.color || "").toLowerCase();
                  if (!c || c === "default") continue;
                  seen.set(c, (seen.get(c) || 0) + (t.yards || 0));
                }
              }
              return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
            })();
            const teeTotals = Object.fromEntries(
              teeColors.map((tc) => [
                tc,
                course.holes.reduce((sum, h) => {
                  const m = (h.tees || []).find((t) => (t.color || "").toLowerCase() === tc);
                  return sum + (m?.yards || 0);
                }, 0),
              ])
            );
            const teeHeaderStyle = (tc) => ({
              background: ({
                blue: "#1e40af", white: "#e5e7eb", yellow: "#facc15",
                red: "#dc2626", black: "#0f172a", gold: "#ca8a04", green: "#15803d",
              })[tc] || "var(--accent-sand)",
              color: ({ white: "#0f172a", yellow: "#0f172a", gold: "#0f172a" })[tc] || "#fff",
            });
            return (
              <table className="w-full text-sm">
                <thead className="bg-[var(--secondary)] text-[var(--primary)]">
                  <tr>
                    <th className="py-2 px-3 text-left font-heading">Hole</th>
                    <th className="py-2 px-3 font-heading">Par</th>
                    <th className="py-2 px-2 font-heading" title="Stroke index — the hole's difficulty ranking (1 = hardest). Used for net scoring / handicap strokes.">SI</th>
                    {teeColors.length > 0 ? (
                      teeColors.map((tc) => (
                        <th key={tc} className="py-2 px-2 text-right font-heading">
                          <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={teeHeaderStyle(tc)} data-testid={`scorecard-tee-${tc}`}>
                            {tc}
                          </span>
                        </th>
                      ))
                    ) : (
                      <th className="py-2 px-3 text-right font-heading">Yds</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {course.holes.map((h) => (
                    <tr key={h.number} className={`border-t border-[var(--border-default)] ${course.stats_unknown ? "text-[var(--text-secondary)] italic" : ""}`} data-testid={`scorecard-row-hole-${h.number}`}>
                      <td className="py-2 px-3 font-mono-num font-bold text-[var(--primary)] not-italic">{h.number}</td>
                      <td className="py-2 px-3 font-mono-num text-center">{course.stats_unknown ? `~${h.par}` : h.par}</td>
                      <td className="py-2 px-2 font-mono-num text-center text-[var(--text-secondary)]" data-testid={`scorecard-si-${h.number}`}>
                        {h.stroke_index || "—"}
                      </td>
                      {teeColors.length > 0 ? (
                        teeColors.map((tc) => {
                          const m = (h.tees || []).find((t) => (t.color || "").toLowerCase() === tc);
                          return (
                            <td key={tc} className="py-2 px-2 font-mono-num text-right">
                              {m?.yards ?? "—"}
                            </td>
                          );
                        })
                      ) : (
                        <td className="py-2 px-3 font-mono-num text-right">{course.stats_unknown ? `~${h.yards}` : h.yards}</td>
                      )}
                    </tr>
                  ))}
                  <tr className="bg-[var(--bg-app)] border-t-2 border-[var(--primary)]">
                    <td className="py-3 px-3 font-heading font-bold text-[var(--primary)]">Total</td>
                    <td className="py-3 px-3 font-mono-num font-bold text-center text-[var(--primary)]">{course.stats_unknown ? "—" : (course.par_total ?? "—")}</td>
                    <td className="py-3 px-2" />
                    {teeColors.length > 0 ? (
                      teeColors.map((tc) => (
                        <td key={tc} className="py-3 px-2 font-mono-num font-bold text-right text-[var(--primary)]">{teeTotals[tc] || "—"}</td>
                      ))
                    ) : (
                      <td className="py-3 px-3 font-mono-num font-bold text-right text-[var(--primary)]">{course.stats_unknown ? "—" : (course.yards_total ?? "—")}</td>
                    )}
                  </tr>
                </tbody>
              </table>
            );
          })()}
        </div>
      </div>

      {showStatsModal && (
        <div
          className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
          data-testid="submit-stats-modal"
          onClick={() => !submitting && setShowStatsModal(false)}
        >
          <div
            className="surface-card max-w-sm w-full p-6 max-h-[90vh] overflow-y-auto my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-heading font-bold text-xl">Submit course stats</h3>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Help every Fairway player by submitting the par + yards for this course. We'll review before publishing.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowStatsModal(false)}
                disabled={submitting}
                className="text-[var(--text-secondary)] hover:text-[var(--text)]"
                aria-label="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="space-y-3 mt-4">
              {/* Photo extractor — Gemini Vision pre-fills par/yards from a snap of the real card */}
              <div className="rounded-2xl border-2 border-dashed border-[var(--primary)]/30 bg-[var(--accent-sand)] p-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-11 w-11 rounded-xl bg-[var(--primary)] text-white flex items-center justify-center shrink-0">
                    <Camera size={22} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm text-[var(--primary)]">
                      {extracting ? "Reading…" : "Add the scorecard photo"}
                    </div>
                    <div className="text-[11px] text-[var(--text-secondary)] leading-snug">
                      Snap the printed card or pick an existing photo — we&apos;ll fill in every hole.
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      capture="environment"
                      disabled={extracting || submitting}
                      data-testid="extract-scorecard-camera"
                      className="hidden"
                      onChange={handleExtractFile}
                    />
                    <span
                      className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-[var(--primary)] text-white text-xs font-bold uppercase tracking-wider ${extracting || submitting ? "opacity-50" : ""}`}
                      data-testid="extract-scorecard-camera-trigger"
                    >
                      <Camera size={14} weight="fill" /> Take photo
                    </span>
                  </label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      disabled={extracting || submitting}
                      data-testid="extract-scorecard-upload"
                      className="hidden"
                      onChange={handleExtractFile}
                    />
                    <span
                      className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full border-2 border-[var(--primary)] text-[var(--primary)] bg-white text-xs font-bold uppercase tracking-wider ${extracting || submitting ? "opacity-50" : ""}`}
                      data-testid="extract-scorecard-upload-trigger"
                    >
                      <Upload size={14} weight="bold" /> Upload
                    </span>
                  </label>
                </div>
                {extractMsg && (
                  <p
                    data-testid="extract-scorecard-msg"
                    className={`text-[11px] mt-2 ${extractMsg.startsWith("✓") ? "text-green-700" : extractMsg.startsWith("⚠") ? "text-amber-700" : "text-red-600"}`}
                  >
                    {extractMsg}
                  </p>
                )}
              </div>

              <p className="text-[10px] text-[var(--text-secondary)] italic text-center -my-1">
                or fill in manually below
              </p>

              <label className="block">
                <span className="label-uppercase block mb-1">Par total</span>
                <input
                  type="number"
                  min={60}
                  max={80}
                  className="input-field"
                  value={submitPar}
                  onChange={(e) => setSubmitPar(e.target.value)}
                  data-testid="submit-stats-par"
                />
              </label>
              <label className="block">
                <span className="label-uppercase block mb-1">Yards total</span>
                <input
                  type="number"
                  min={3000}
                  max={9000}
                  className="input-field"
                  value={submitYds}
                  onChange={(e) => setSubmitYds(e.target.value)}
                  data-testid="submit-stats-yards"
                />
              </label>

              <label className="block">
                <span className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Which tee did you play?</span>
                <select
                  value={submitTee || ""}
                  onChange={(e) => {
                    const newPrimary = e.target.value;
                    // Prevent the same colour appearing as both primary AND an extra tee.
                    if (newPrimary) {
                      setExtraTees((arr) => arr.filter((t) => t.label !== newPrimary));
                    }
                    setSubmitTee(newPrimary);
                  }}
                  data-testid="submit-stats-tee"
                  className="input-field"
                >
                  <option value="">— Pick the tee colour —</option>
                  <option value="blue">Blue (often longest)</option>
                  <option value="white">White</option>
                  <option value="yellow">Yellow</option>
                  <option value="red">Red (often shortest)</option>
                  <option value="black">Black / Tips</option>
                  <option value="gold">Gold</option>
                  <option value="green">Green</option>
                </select>
                <span className="text-[10px] text-[var(--text-secondary)] italic">Helps the app give the right yardages to the next mate who picks the same tee.</span>
              </label>

              {/* Optional WHS Course Rating + Slope for the primary tee.
                  Powers the proper Handicap Index → Course Handicap conversion
                  (round(Index × Slope/113 + (CR − Par))). Auto-filled by OCR
                  when Gemini can read them off the card. */}
              <div className="flex gap-2" data-testid="submit-stats-whs-row">
                <label className="flex-1 flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">
                    Course Rating <span className="opacity-70 normal-case">(WHS · optional)</span>
                  </span>
                  <input
                    type="number"
                    step="0.1"
                    min={55}
                    max={85}
                    value={submitCourseRating}
                    onChange={(e) => setSubmitCourseRating(e.target.value)}
                    placeholder="e.g. 71.2"
                    data-testid="submit-stats-course-rating"
                    className="input-field !py-2"
                  />
                </label>
                <label className="flex-1 flex flex-col gap-1">
                  <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">
                    Slope <span className="opacity-70 normal-case">(WHS · optional)</span>
                  </span>
                  <input
                    type="number"
                    min={55}
                    max={155}
                    value={submitSlopeRating}
                    onChange={(e) => setSubmitSlopeRating(e.target.value)}
                    placeholder="e.g. 130"
                    data-testid="submit-stats-slope-rating"
                    className="input-field !py-2"
                  />
                </label>
              </div>
              <span className="text-[10px] text-[var(--text-secondary)] italic -mt-1 block">
                Unlocks proper per-hole handicap strokes for you & your mates. Leave blank if not on the card.
              </span>

              {/* Multi-tee picker — extra tee colours visible on the same scorecard. */}
              <div data-testid="submit-stats-extra-tees">
                <span className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider block mb-1.5">
                  Also reading from this card?
                </span>
                <p className="text-[10px] text-[var(--text-secondary)] italic mb-2">
                  Tick any other tee colours you can see on the scorecard so we can save them all in one go.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: "blue",   label: "Blue",   swatch: "#3B82F6", textOnSwatch: "#fff" },
                    { id: "white",  label: "White",  swatch: "#ffffff", textOnSwatch: "#143D2F" },
                    { id: "yellow", label: "Yellow", swatch: "#FFE45E", textOnSwatch: "#143D2F" },
                    { id: "red",    label: "Red",    swatch: "#ef4444", textOnSwatch: "#fff" },
                    { id: "black",  label: "Black",  swatch: "#111111", textOnSwatch: "#fff" },
                    { id: "gold",   label: "Gold",   swatch: "#D4AF37", textOnSwatch: "#143D2F" },
                    { id: "green",  label: "Green",  swatch: "#16a34a", textOnSwatch: "#fff" },
                  ].map((opt) => {
                    const isPrimary = submitTee === opt.id;
                    const idx = extraTees.findIndex((t) => t.label === opt.id);
                    const isExtra = idx >= 0;
                    const isOn = isPrimary || isExtra;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        disabled={isPrimary}
                        title={isPrimary ? "Already selected as your played tee" : undefined}
                        onClick={() => {
                          if (isPrimary) return;
                          if (isExtra) {
                            setExtraTees((arr) => arr.filter((_, i) => i !== idx));
                          } else {
                            setExtraTees((arr) => [
                              ...arr,
                              {
                                label: opt.id,
                                holes: Array.from({ length: 18 }, (_, i) => ({
                                  number: i + 1,
                                  yards: perHole[i]?.yards ?? 380,
                                })),
                              },
                            ]);
                            setShowPerHole(true);
                          }
                        }}
                        data-testid={`submit-stats-extra-tee-${opt.id}`}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-all ${
                          isOn
                            ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]"
                            : "border-[var(--text-secondary)]/30 text-[var(--text-secondary)]"
                        } ${isPrimary ? "opacity-50" : ""}`}
                      >
                        <span
                          className="h-3 w-3 rounded-full border border-black/20 shrink-0"
                          style={{ background: opt.swatch }}
                        />
                        {opt.label}
                        {isPrimary && <span className="text-[9px] opacity-70">(played)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowPerHole((v) => !v)}
                data-testid="submit-stats-toggle-per-hole"
                className="text-xs font-bold uppercase tracking-wider text-[var(--primary)] hover:underline mt-1"
              >
                {showPerHole ? "↑ Hide per-hole breakdown" : "↓ Add per-hole breakdown (optional)"}
              </button>

              {showPerHole && (
                <div data-testid="submit-stats-per-hole-table">
                  <p className="text-[10px] text-[var(--text-secondary)] mb-2 italic">
                    Per-hole values are best — they fix the &quot;every par-3 shows 200 yds&quot; placeholder.
                    {extraTees.length > 0 && " One yards column per tee colour."}
                  </p>
                  {(() => {
                    const siValues = perHole.map((h) => h.stroke_index).filter((v) => v !== null && v !== undefined);
                    const siFilled = siValues.length;
                    const siDupes = new Set(
                      siValues.filter((v, i, arr) => arr.indexOf(v) !== i)
                    );
                    return (
                      <div className="flex items-center gap-2 mb-2 text-[10px] flex-wrap">
                        <span
                          data-testid="submit-stats-si-progress"
                          className={`px-2 py-0.5 rounded ${
                            siFilled === 18
                              ? "bg-green-500/20 text-green-300"
                              : "bg-white/5 text-[var(--text-secondary)]"
                          }`}
                        >
                          SI · {siFilled}/18 set
                        </span>
                        {siDupes.size > 0 && (
                          <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-300" data-testid="submit-stats-si-dupes">
                            Duplicate SI: {[...siDupes].sort((a, b) => a - b).join(", ")}
                          </span>
                        )}
                        {siFilled === 0 && (
                          <span className="italic text-[var(--text-secondary)]">
                            Optional — admin will suggest one if left blank
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <div
                    className="grid gap-1.5 items-center text-xs mb-1 font-bold text-[var(--text-secondary)] uppercase tracking-wider"
                    style={{ gridTemplateColumns: `28px 50px 50px repeat(${1 + extraTees.length}, minmax(0, 1fr))` }}
                  >
                    <span>#</span>
                    <span className="text-center">Par</span>
                    <span className="text-center">SI</span>
                    <span className="text-right pr-1">
                      Yds<span className="block text-[8px] opacity-70 font-normal normal-case">{submitTee || "main"}</span>
                    </span>
                    {extraTees.map((t) => (
                      <span key={t.label} className="text-right pr-1">
                        Yds<span className="block text-[8px] opacity-70 font-normal normal-case">{t.label}</span>
                      </span>
                    ))}
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {perHole.map((h, i) => {
                      const siDupe = h.stroke_index !== null && perHole.filter((x) => x.stroke_index === h.stroke_index).length > 1;
                      return (
                      <div
                        key={h.number}
                        className="grid gap-1.5 items-center"
                        style={{ gridTemplateColumns: `28px 50px 50px repeat(${1 + extraTees.length}, minmax(0, 1fr))` }}
                      >
                        <span className="font-mono-num font-bold text-sm text-[var(--primary)]">{h.number}</span>
                        <select
                          value={h.par}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            const next = [...perHole];
                            next[i] = { ...next[i], par: v };
                            setPerHole(next);
                            setSubmitPar(String(next.reduce((s, x) => s + x.par, 0)));
                          }}
                          data-testid={`submit-stats-hole-${h.number}-par`}
                          className="input-field !py-1.5 !px-2 !text-sm text-center"
                        >
                          {[3, 4, 5, 6].map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <select
                          value={h.stroke_index ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const v = raw === "" ? null : Number(raw);
                            const next = [...perHole];
                            next[i] = { ...next[i], stroke_index: v };
                            setPerHole(next);
                          }}
                          data-testid={`submit-stats-hole-${h.number}-si`}
                          className={`input-field !py-1.5 !px-2 !text-sm text-center ${siDupe ? "!border-red-400 !text-red-300" : ""}`}
                        >
                          <option value="">—</option>
                          {Array.from({ length: 18 }, (_, idx) => idx + 1).map((si) => (
                            <option key={si} value={si}>{si}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min={80}
                          max={750}
                          value={h.yards}
                          onChange={(e) => {
                            const v = Number(e.target.value || 0);
                            const next = [...perHole];
                            next[i] = { ...next[i], yards: v };
                            setPerHole(next);
                            setSubmitYds(String(next.reduce((s, x) => s + (x.yards || 0), 0)));
                          }}
                          data-testid={`submit-stats-hole-${h.number}-yards`}
                          className="input-field !py-1.5 !px-2 !text-sm text-right"
                        />
                        {extraTees.map((t, ti) => (
                          <input
                            key={t.label}
                            type="number"
                            min={80}
                            max={750}
                            value={t.holes[i]?.yards ?? 380}
                            onChange={(e) => {
                              const v = Number(e.target.value || 0);
                              setExtraTees((arr) => {
                                const next = [...arr];
                                const teeNext = { ...next[ti] };
                                const holes = [...teeNext.holes];
                                holes[i] = { ...holes[i], yards: v };
                                teeNext.holes = holes;
                                next[ti] = teeNext;
                                return next;
                              });
                            }}
                            data-testid={`submit-stats-hole-${h.number}-yards-${t.label}`}
                            className="input-field !py-1.5 !px-2 !text-sm text-right"
                          />
                        ))}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            {submitMsg && (
              <p
                data-testid="submit-stats-msg"
                className={`text-xs mt-3 ${submitMsg.startsWith("✓") ? "text-green-700" : "text-red-600"}`}
              >
                {submitMsg}
              </p>
            )}
            <button
              type="button"
              disabled={submitting}
              onClick={async () => {
                setSubmitting(true);
                setSubmitMsg("");
                try {
                  // Client-side SI validation to fail fast with a friendly toast
                  // instead of round-tripping to the server for a 400.
                  if (showPerHole) {
                    const siValues = perHole.map((h) => h.stroke_index).filter((v) => v !== null && v !== undefined);
                    if (siValues.length > 0 && siValues.length < 18) {
                      setSubmitMsg("Stroke Index must be filled for every hole or none");
                      setSubmitting(false);
                      return;
                    }
                    if (siValues.length === 18 && new Set(siValues).size !== 18) {
                      setSubmitMsg("Stroke Index values must be unique 1–18");
                      setSubmitting(false);
                      return;
                    }
                  }
                  // Build a multi-tee `tees[]` payload when the user has ticked
                  // any extras. The primary tee goes first; extras follow.
                  // When no tee is picked at all we fall back to legacy single-tee.
                  const primaryCR = submitCourseRating !== "" ? Number(submitCourseRating) : null;
                  const primarySlope = submitSlopeRating !== "" ? Number(submitSlopeRating) : null;
                  const teesPayload = [];
                  if (submitTee && showPerHole) {
                    teesPayload.push({
                      label: submitTee,
                      yards_total: perHole.reduce((s, x) => s + (x.yards || 0), 0),
                      course_rating: primaryCR,
                      slope_rating: primarySlope,
                      holes: perHole.map((h) => ({ number: h.number, yards: h.yards })),
                    });
                  }
                  for (const t of extraTees) {
                    teesPayload.push({
                      label: t.label,
                      yards_total: t.holes.reduce((s, x) => s + (x.yards || 0), 0),
                      course_rating: t.course_rating ?? null,
                      slope_rating: t.slope_rating ?? null,
                      holes: t.holes.map((h) => ({ number: h.number, yards: h.yards })),
                    });
                  }
                  const holesPayload = showPerHole
                    ? perHole.map((h) => ({
                        number: h.number,
                        par: h.par,
                        yards: h.yards,
                        stroke_index: h.stroke_index ?? null,
                      }))
                    : null;
                  const { data: submitResp } = await api.post(`/courses/${id}/submit-stats`, {
                    par_total: Number(submitPar),
                    yards_total: Number(submitYds),
                    holes: holesPayload,
                    tee_label: submitTee || null,
                    tees: teesPayload.length > 0 ? teesPayload : null,
                    ocr_confidence: ocrConfidence || null,
                  });
                  // Re-fetch the user's submission so the page repaints with
                  // the new values + "Pending review" banner immediately.
                  try {
                    const { data: subData } = await api.get(`/courses/${id}/my-submission`);
                    setMySubmission(subData.submission || null);
                  } catch { /* non-fatal: banner will appear on next page load */ }
                  const n = teesPayload.length;
                  if (submitResp?.auto_approved) {
                    setSubmitMsg(
                      n > 1
                        ? `✓ Scorecard verified — ${n} tees live on the course now!`
                        : "✓ Scorecard verified — course is live now!"
                    );
                    // Force a refresh so the newly-approved par/yards show up
                    setTimeout(() => window.location.reload(), 1200);
                  } else {
                    setSubmitMsg(
                      n > 1
                        ? `✓ Thanks! ${n} tees queued for review.`
                        : "✓ Thanks! Your submission is in the queue."
                    );
                  }
                  setTimeout(() => setShowStatsModal(false), 1500);
                } catch (err) {
                  setSubmitMsg(formatApiError(err));
                } finally {
                  setSubmitting(false);
                }
              }}
              data-testid="submit-stats-submit"
              className="btn-primary w-full mt-4"
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, testid }) {
  return (
    <div className="surface-card-flat p-3 text-center" data-testid={testid}>
      <div className="label-uppercase">{label}</div>
      <div className="font-mono-num text-2xl font-black text-[var(--primary)]">{value}</div>
    </div>
  );
}

/*
  Small "todo item" line for the confidence card. `done` toggles between a
  green tick + strikethrough text and an amber circle + call-to-action text.
  Keeps the confidence card scannable: users can see exactly what's still
  needed to push a course from red → amber → green.
*/
function ChecklistItem({ done, text }) {
  return (
    <div
      className="flex items-center gap-2 text-[11px] leading-snug"
      data-testid={done ? "verify-check-done" : "verify-check-todo"}
    >
      <span
        className="inline-flex items-center justify-center h-4 w-4 rounded-full shrink-0"
        style={{
          background: done ? "rgba(16,185,129,.18)" : "rgba(217,119,6,.14)",
          color: done ? "#0F9C6C" : "#B45309",
          fontSize: 9,
          fontWeight: 900,
        }}
      >
        {done ? "✓" : "○"}
      </span>
      <span
        className={done ? "text-[var(--text-secondary)] line-through" : "text-[var(--primary)] font-medium"}
      >
        {text}
      </span>
    </div>
  );
}
